<script setup lang="ts">
import type { IRate, IValue } from "~/interfaces";
import Checkbox from "~/components/inputs/Checkbox.vue";
import { useFormContainer } from "~/composables/useFormContainer";
import FormCard from "~/components/ui/FormCard.vue";
import RadioButton from "~/components/inputs/RadioButton.vue";
import InputFloatingLabel from "~/components/inputs/InputFloatingLabel.vue";
import Accordion from "~/components/ui/Accordion.vue";

import Validator from "~/utils/validator";

import { useVuelidate } from "@vuelidate/core";
const { locale } = await useLocale("forms", "payment_data", true);
import { helpers } from "@vuelidate/validators";
import paymentDataFormRules from "~/rules/paymentData";
import cyberWebyConsent from "~/rules/cyberWebyConsent";
import { yesNoOptions } from "~/utils/values";
import { getModelPromoKO } from "~/utils/promo";
import Perks from "~/components/static/Perks.vue";

import { clubConsent, clubDecline } from "~/utils/service";
import { updateStateValue } from "~/utils";
import { CLUB_DISCOVER, CLUB_RULES_URL, CYBERWEBY, CYBERWEBY_PRIVACY_URL, CYBERWEBY_TERMS_URL } from "~/utils/constants";

defineComponent({
  name: "PaymentData",
});


const { installationDebitDates, showPEPExplanation, requiredABI, installment, showClubUpsell, showCyberUpsell, isRev, installationDate, hideDebitDateNote } = defineProps({
  progress: { type: Number, required: true },
  installationDate: { type: Number, required: false },
  title: { type: String, required: true },
  politicallyExposedOptions: {
    type: Array as () => IValue[],
    default: () => [...yesNoOptions].reverse(),
  },
  prepaidCards: { type: Boolean, default: false },
  submitText: { type: String, required: true },
  showHelperText: { type: Function, default: () => false },
  installationDebitDates: { type: Array<IValue> },
  showPEPExplanation: { type: Boolean, default: true },
  ibanDisabled: { type: Boolean, default: false },
  requiredABI: { type: String, required: false },
  installment: { type: String, required: false, default: "0.00" },
  showClubUpsell: { type: Boolean, default: false },
  showCyberUpsell: { type: Boolean, default: false },
  isRev: { type: Boolean, default: false },
  hideDebitDateNote: { type: Boolean, default: false }
});

const { FormContainer } = useFormContainer();

const installmentDebitDay = useState<number>("installmentDebitDay", () => installationDate ?? 2);

const politicallyExposedPerson = useState<boolean>("politicallyExposedPerson");
const iban = useState<string | null>("iban", () => null);
const forgotIbanAccordion = ref<any>();
const cyberAccordion = ref<any>();
const modelKO = ref<IRate | null>(getModelPromoKO());

const buyClubSelection = useState<boolean | null>("clubSelection", () => null);
const buyCyberSelection = useState<boolean | null>("cyberSelectionUpsell", () => null);

const consent_cyber_main = useState<boolean | null>("consent_cyber_main");
const consent_cyber1 = useState<boolean | null>("consent_cyber1");
const consent_cyber2 = useState<boolean | null>("consent_cyber2");
const consent_cyber3 = useState<boolean | null>("consent_cyber3");

const cyberLocale = await getLocale("forms", "cyberweby");


watch(buyCyberSelection, (newVal) => {
  if (newVal !== null) {
    consent_cyber_main.value = newVal;
    consent_cyber1.value = newVal;
    consent_cyber2.value = newVal;
    consent_cyber3.value = newVal;
  }
});


const handleFinalSubmit = () => {
  const isValid = validate();
  if (isValid) {
    if (showClubUpsell) {
      if (buyClubSelection.value === true) {
        clubConsent();
      } else {
        clubDecline();
      }
    }
    if (showCyberUpsell) {
      if (buyCyberSelection.value === true) {
        updateStateValue("serviceCyber", true);
        updateStateValue("servicePackCyber", CYBERWEBY.code);
      } else {
        updateStateValue("serviceCyber", false);
      }
    }

    emit("submit");
  }
};
const toggleAccordion = () => {
  forgotIbanAccordion.value.toggleAccordion();
};

const v$ = useVuelidate(
  paymentDataFormRules,
  {
    installmentDebitDay,
    politicallyExposedPerson,
    iban,
    showPEPExplanation,
    requiredABI,
    buyClubSelection,
    showClubUpsell,
    buyCyberSelection,
    showCyberUpsell,
    consent_cyber_main,
    consent_cyber1,
    consent_cyber2,
    consent_cyber3
  }
);

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};

const emit = defineEmits(["init", "submit"]);
onMounted(() => {

  updateStateValue("installmentDebitDay", installationDate ?? 2);
  
  buyCyberSelection.value = null;
  if (!showCyberUpsell) {
    consent_cyber_main.value = null;
    consent_cyber1.value = null;
    consent_cyber2.value = null;
    consent_cyber3.value = null;
  }
  emit("init")
});

</script>

<template>
  <FormContainer :progress="progress" class="payment-data-container">
    <FormCard :submit-text="submitText" @submit="handleFinalSubmit" :is-padding-x=false>

      <div class="payment-banner bg-yellow text-center md:text-left py-4 mb-4 -mt-4 w-full sm:px-6 md:px-8">
        <h3 class="text-xl md:text-2xl font-semibold text-gray-700 leading-tight mx-4">
          <span>{{ locale?.banner }}</span>
        </h3>
      </div>
      <h1 class="pageTitle px-4 sm:px-6 md:px-8 mx-4" v-html="locale?.title_iban" />
      <p v-html="locale?.subtitle_iban" class="pageSubtitle px-4 sm:px-6 md:px-8 mx-4" />
      <div class="text-sm w-full xl:pt-2 mb-8  justify-start items-center text-gray-700 px-4 sm:px-6 md:px-8 mx-4">
        <div class="w-full lg:w-1/2 text-gray-700 flex flex-col">
          <InputFloatingLabel v-model="iban" :label="locale?.iban_current_account" type="text" :mask="ibanMask"
            :disabled="ibanDisabled" :error="v$.iban.$error" :error-message="Validator.message(v$.iban)" test-id="iban"
            @error="(e: any) => console.log('error')" />
          <p v-if="!prepaidCards" class="block italic text-sm ">
            {{ locale?.prepaid_no_accepted }}
          </p>
          <div class="text-sm " :class="{ 'mt-2': prepaidCards }">
            <span @click="toggleAccordion" @keydown="e => { e.key == 'Enter' && toggleAccordion() }"
              v-html="locale?.forgot_iban" />
          </div>
        </div>
        <div class="w-full">
          <div class="text-xs">
            <Accordion ref="forgotIbanAccordion">
              <template #items>
                <div class="yellowBox mt-5 flex flex-wrap">
                  <div class="w-full md:w-1/3">
                    <h3 class="font-semibold mb-3 text-lg">
                      {{ locale?.find_iban_code }}
                    </h3>
                  </div>
                  <div class="w-full md:w-1/3">
                    <p class="text-sm">
                      {{ locale?.you_can_find_iban_code }}
                    </p>
                    <ul class="list-disc px-6 my-2 text-sm">
                      <li>
                        {{ locale?.banking_service }}
                      </li>
                      <li>
                        {{ locale?.statement_extract }}
                      </li>
                      <li>{{ locale?.pay_stub }}</li>
                    </ul>
                  </div>
                  <div class="w-full md:w-1/3">
                    <div class="flex flex-col text-sm mb-4">
                      {{ locale?.iban_example }} <br />
                      {{ locale?.iban_example_code }}
                    </div>
                    <span class="block pb-4 text-xs">
                      {{ locale?.code_format }}
                    </span>
                  </div>
                </div>
              </template>
            </Accordion>
          </div>
        </div>
      </div>
      <div class="px-4 sm:px-6 md:px-8 mx-4 w-full md:w-1/2">
        <div class="yellowBox w-full mb-8 !shadow-md">
          <h3 class="font-semibold mb-3 text-lg">
            Quando pagherò la rata?
          </h3>
          <p class="text-sm">
            L'addebito avverrà automaticamente 
            <b>il giorno {{ isRev ? '15' : (installationDate === 1 ? '1' : '10') }}</b> 
            del mese sul conto indicato. <span v-if="!isRev && !isBank() && !hideDebitDateNote"> Potrai modificare la data nella tua Area Riservata.</span>
          </p>
        </div>
      </div>

      <template v-if="showPEPExplanation">
        <div class="px-4 sm:px-6 md:px-8 mx-4 mb-6">

          <div class="flex flex-col sm:flex-row sm:items-start sm:gap-6">

            <div class="w-full text-sm mb-4 sm:mb-0 sm:order-2">
              Sei una Persona Politicamente Esposta? Per legge, dobbiamo sapere se tu, un tuo familiare o un tuo
              contatto stretto ricoprite o avete ricoperto nell'ultimo anno una carica politica.

              <div v-if="politicallyExposedPerson" class="block mt-2 text-red text-[13px]"
                v-html="locale?.verify_pep_definition" />

              <Accordion>
                <div class="text-sm w-full mt-2" v-html="locale?.pep_definition" />
                <template #items>
                  <div class="py-5 text-sm yellowBox mt-5" v-html="locale?.pep_explanation" />
                </template>
              </Accordion>

            </div>

            <RadioButton v-model="politicallyExposedPerson" :values="politicallyExposedOptions"
              :error="v$.politicallyExposedPerson.$error"
              :error-message="Validator.message(v$.politicallyExposedPerson)" data-testid="politicallyExposedPerson"
              class="sm:order-1 sm:shrink-0" :errorMessageIsSmall="true" />
          </div>
        </div>
      </template>
      <template v-if="showClubUpsell">
        <div class="px-4 sm:px-6 md:px-8 mx-4 mb-6">
          <div class="flex flex-col sm:flex-row sm:items-start sm:gap-6">

            <div class="w-full text-sm mb-4 sm:mb-0 sm:order-2">
              Acconsento all'attivazione del <a :href="CLUB_DISCOVER" target="_blank" class="underline">servizio
                CLUB</a> ad un prezzo esclusivo di {{ installment }}€ al mese applicato automaticamente alla mia rata.
              Prima di procedere ti invitiamo a prendere visione del <a :href="CLUB_RULES_URL" target="_blank"
                class="underline">Regolamento</a> e dell'<a :href="CLUB_PRIVACY_URL" target="_blank"
                class="underline">informativa privacy</a>. Il servizio CLUB è facoltativo e non è necessario per
              ottenere il prestito o per ottenerlo a condizioni più vantaggiose
            </div>

            <RadioButton v-model="buyClubSelection" :values="yesNoOptions" :error="v$.buyClubSelection.$error"
              :error-message="Validator.message(v$.buyClubSelection)" data-testid="radio-club-upsell"
              class="sm:order-1 sm:shrink-0" :errorMessageIsSmall="true" />
          </div>
        </div>
      </template>


      <template v-if="showCyberUpsell">
        <div class="px-4 sm:px-6 md:px-8 mx-4 mb-6 text-gray-700 cyberweby-section">
          <div class="flex flex-col sm:flex-row sm:items-start sm:gap-6">
            <div class="w-full text-sm mb-4 sm:mb-0 sm:order-2">
              <p class="mb-2">
                Acconsento all'attivazione del servizio CyberWeby, finanziato mediante utilizzo della Linea di Credito.
                Confermo i trattamenti dei dati e tutte le dichiarazioni obbligatorie
                (<span class="underline cursor-pointer " @click="cyberAccordion?.toggleAccordion()">clicca
                  qui</span> per vederle tutte).
                Inoltre, ti invitiamo a prendere visione del
                <a :href="CYBERWEBY_TERMS_URL" target="_blank" class="underline">Regolamento</a> e dell'
                <a :href="CYBERWEBY_PRIVACY_URL" target="_blank" class="underline">Informativa Privacy</a>.
                Il servizio CyberWeby è facoltativo e non è necessario per ottenere la Linea di Credito.
              </p>

              <div class="mt-4">
                <Accordion ref="cyberAccordion">
                  <template #items>
                    <div class="pt-4">
                      <Checkbox v-model="consent_cyber1" class="mb-4" :error="v$.consent_cyber1.$error"
                        :error-message="Validator.message(v$.consent_cyber1)">
                        <div v-html="cyberLocale?.consents.consent1.replace('%d', CYBERWEBY_TERMS_URL)"></div>
                      </Checkbox>
                      <Checkbox v-model="consent_cyber2" class="mb-4" :error="v$.consent_cyber2.$error"
                        :error-message="Validator.message(v$.consent_cyber2)">
                        <div v-html="cyberLocale?.consents.consent2.replace('%d', CYBERWEBY_TERMS_URL)"></div>
                      </Checkbox>
                      <Checkbox v-model="consent_cyber3" :error="v$.consent_cyber3.$error"
                        :error-message="Validator.message(v$.consent_cyber3)">
                        <div v-html="cyberLocale?.consents.consent3.replace('%d', CYBERWEBY_PRIVACY_URL)"></div>
                      </Checkbox>
                    </div>
                  </template>
                </Accordion>
              </div>
            </div>

            <RadioButton v-model="buyCyberSelection" :values="yesNoOptions" :error="v$.buyCyberSelection.$error"
              :error-message="Validator.message(v$.buyCyberSelection)" data-testid="radio-cyber-upsell"
              class="sm:order-1 sm:shrink-0" :errorMessageIsSmall="true" />
          </div>
        </div>
      </template>
      <div v-if="showHelperText() && modelKO" class="w-full mt-6 px-4 sm:px-6 md:px-8 mx-4">
        <p class="text-sm text-left">
          Cliccando su Invia richiesta prendo atto che il mio profilo non
          soddisfa i
          <button type="button" class="underline inline" @click="updateStateValue('promoModal', true)">
            Termini e condizioni
          </button>
          per richiedere l’offerta promo e che invio richiesta di
          {{ modelKO.imp_finanz_text }} TAEG {{ modelKO.taeg }}%.
        </p>
      </div>
    </FormCard>
    <div class="spacer h-6" />
    <div class="spacer h-6" />
  </FormContainer>
  <Perks />
</template>

<style>
.cyberweby-section .small-checkbox.error label {
  padding-bottom: 0.1em !important;
}

.cyberweby-section .small-checkbox label>div {
  @apply text-xs;
}

.cyberweby-section .small-checkbox.error label>div {
  @apply pb-0.5;
}

.cyberweby-section .small-checkbox label>div[data-testid=consentAccordion] {
  @apply text-sm;
}
</style>
